// =============================================================================
// Copyright   : 2018-2021 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf, Andreas Hesse
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : dialogs
// =============================================================================
// ---Third-party imports
import React, {useState} from 'react'
import {Button, Modal, Header, Transition} from 'semantic-ui-react'
import {Rnd} from 'uix/chunks'
import KeyHandler, {KEYDOWN} from 'react-key-handler'
import clsx from 'clsx'
import {isElement} from 'react-is'

// ---Framework imports
import {isNumber, callIfFunc, isNil} from 'ldash'
import {isSSR} from 'uix/utils'
import {
  usePersistentShallow,
  useFullscreen,
  useMemo,
  useFullscreenContext,
  FullscreenContext,
} from 'uix/hooks'
import log from 'uix/logging'
import {VIcon} from 'uix/graphics'

// ---Local imports
import {AutoPortal} from './portals'
import styles from './dialog.scss'

// ---Exports
const ButtonCancel = props => <Button inverted color="grey" content="Abbrechen" {...props} />
const ButtonOk = props => <Button content="Ok" {...props} />

export const Dialog = ({
  size = 'large', // why large?
  open,
  icon,
  header,
  subheader,
  content,
  onClose,
  onCancel,
  onSubmit, // on OK button click
  submitDisabled = false, // set to true if ok button should be inactive
  children,
  buttons, // external buttons
  style,
  trigger, // compatibility with XPopup
}) => {
  let {domNode} = useFullscreenContext()
  let [triggerOpen, setTriggerOpen] = useState(false)
  let triggerEl = null

  if (isElement(trigger)) {
    // assume single element like button
    triggerEl = React.cloneElement(trigger, {onClick: () => setTriggerOpen(!triggerOpen)})
  } else if (trigger) {
    log.warn('bad trigger, should be a single react element')
  }
  let onSubmitClick = onSubmit
  let onCancelClick = onCancel
  if (trigger) {
    if (isNil(open)) {
      open = triggerOpen
    }
    onClose = () => {
      setTriggerOpen(false)
    }
    onSubmitClick = () => {
      setTriggerOpen(false)
      callIfFunc(onSubmit)
    }
    onCancelClick = () => {
      setTriggerOpen(false)
      callIfFunc(onCancel)
    }
  }
  let actions = buttons || onCancel || onSubmit

  return (
    <>
      {triggerEl}
      <Modal
        style={style}
        size={size}
        open={open}
        onClose={onClose}
        mountNode={domNode || undefined}
      >
        {(header || icon) && (
          <Header>
            {icon && <VIcon name={icon} />}
            <Header.Content>
              {header}
              {subheader && <Header.Subheader>{subheader}</Header.Subheader>}
            </Header.Content>
          </Header>
        )}
        <Modal.Content scrolling>{children || content}</Modal.Content>
        {actions && (
          <Modal.Actions>
            {buttons ? (
              buttons
            ) : (
              <>
                {onCancel && <ButtonCancel onClick={onCancelClick} />}
                {onSubmit && <ButtonOk onClick={onSubmitClick} disabled={submitDisabled} />}
              </>
            )}
          </Modal.Actions>
        )}
      </Modal>
    </>
  )
}

Dialog.ButtonCancel = ButtonCancel
Dialog.ButtonOk = ButtonOk

export const TransitionDialog = ({
  open,
  style,
  position = 'top',
  children,
  animation = 'fade',
  duration = 300,
  //animation: slide, fly, fade, swing
  //positions: top, bottom, left, right
}) => {
  const styles = {
    [position]: '-2px',
    ...style,
  }

  const animations = {
    top: `${animation} down`,
    bottom: `${animation} up`,
    left: `${animation} right`,
    right: `${animation} left`,
  }[position]

  return (
    <div>
      <Transition.Group animation={animations} duration={duration}>
        {open && (
          <Modal style={styles} open={open} size="mini">
            {children}
          </Modal>
        )}
      </Transition.Group>{' '}
    </div>
  )
}

const INITIAL_OPTIONS = {size: {width: null, height: null}, position: {x: null, y: null}}
export const ResizableDialog = ({
  name = 'unnamedDlg',
  storeKey, // need this if you have multiple (unnamed) dialogs on the same Part
  children,
  className,
  header,
  icon,
  onClose,
  closeOnEsc = false,
  closeButton = true,
  fullscreenButton = true,
  overflow = 'auto',
  flexInner = false,
  minWidth = 240,
  minHeight = 120,
  onSubmit = false,
  initialPosition = null, // use screen center if null
  initialSize = {width: 640, height: 400},
  trigger, // use external react element for control openess
}) => {
  /* a dialog with dragging & resizing support
   */
  let [triggerOpen, setTriggerOpen] = useState(false)

  let {isFullscreen: inFullscreenContext, ref: inFullscreenRef} = useFullscreenContext()
  let portalTarget = inFullscreenContext ? 'fullscreenRootPortal' : 'rootPortal'

  let {ref, toggleFullscreen, isFullscreen} = useFullscreen()

  let fullscreenContextValue = {isFullscreen, ref}
  if (!isFullscreen && inFullscreenContext) {
    // pass through context info if we are in outer fullscreen
    fullscreenContextValue = {isFullscreen: true, ref: inFullscreenRef}
  }
  let [opts, setOpts] = usePersistentShallow(`${storeKey || name}`, INITIAL_OPTIONS, 3)

  let {position, size} = useMemo(() => {
    let size = {width: opts.size.width, height: opts.size.height}

    if (!isNumber(size.width)) {
      size.width = initialSize.width
    }
    if (!isNumber(size.height)) {
      size.height = initialSize.height
    }

    let {x, y} = opts.position
    if (!initialPosition) {
      let [iw, ih] = isSSR ? [1600, 900] : [window.innerWidth, window.innerHeight]

      if (!isNumber(x)) {
        // centered
        x = iw / 2 - size.width / 2
      }
      if (!isNumber(y)) {
        // 30% from top
        y = ih / 3 - size.height / 2
      }
      x = Math.max(Math.min(x, iw - size.width), 0)
      y = Math.max(Math.min(y, ih - size.height), 0)
    } else {
      if (!isNumber(x)) {
        x = initialPosition.x
      }
      if (!isNumber(y)) {
        y = initialPosition.y
      }
    }

    return {position: {x, y}, size}
  }, [opts.position, opts.size])

  let handleClass = `dialog_handle_${name}`
  let innerStyle = {overflow}
  if (flexInner) {
    // flexDirection=row works nicely with react-table (see StoreCheckResult)
    innerStyle = {...innerStyle, display: 'flex', flex: 1, flexDirection: 'row'}
  }

  let close = () => {
    setTriggerOpen(false)
    callIfFunc(onClose)
  }
  // we do want do want to propagate doubleClicks further upward
  // i'v seen bad fullscreen switches on nested dialogs
  let catchDoubleClicks = event => event.stopPropagation()

  let dialog = (
    <AutoPortal target={portalTarget}>
      <Rnd
        bounds="window"
        className={clsx(className, styles.dialog)}
        size={size}
        style={{display: 'flex'}}
        position={position}
        onDragStop={(e, d) => {
          setOpts({position: {x: d.x, y: d.y}})
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          setOpts({size: {width: ref.offsetWidth, height: ref.offsetHeight}, position})
        }}
        dragHandleClassName={handleClass}
        minWidth={minWidth}
        minHeight={minHeight}
        onDoubleClick={catchDoubleClicks}
      >
        {closeOnEsc && <KeyHandler keyEventName={KEYDOWN} code={'Escape'} onKeyHandle={close} />}
        <div
          className={clsx(handleClass, styles.header)}
          onDoubleClick={() => (fullscreenButton ? callIfFunc(toggleFullscreen) : null)}
        >
          <h3 className={styles.headerText}>
            {icon && <VIcon className={styles.headerIcon} name={icon} />}
            {header}
          </h3>
          <div className={styles.buttons}>
            {fullscreenButton && (
              <Button
                icon
                onClick={toggleFullscreen}
                className={clsx(styles.headerButton, styles.maximize)}
                secondary
              >
                <VIcon name="square" />
              </Button>
            )}
            {closeButton && (
              <Button
                icon
                secondary
                onClick={event => {
                  event.stopPropagation()
                  close()
                }}
                className={clsx(styles.headerButton, styles.close)}
              >
                <VIcon name="close" />
              </Button>
            )}
          </div>
        </div>

        <div
          ref={ref}
          className={clsx(styles.content, isFullscreen && styles.fullscreen)}
          style={innerStyle}
        >
          <FullscreenContext.Provider value={fullscreenContextValue}>
            {children}
          </FullscreenContext.Provider>
        </div>
        {onSubmit && (
          <div className={styles.actions}>
            <Button
              color="grey"
              style={{
                float: 'right',
              }}
              onClick={onSubmit}
            >
              OK
            </Button>
          </div>
        )}
      </Rnd>
    </AutoPortal>
  )

  if (trigger) {
    trigger = isElement(trigger)
      ? React.cloneElement(trigger, {onClick: () => setTriggerOpen(true)})
      : 'bad trigger'
    return (
      <>
        {trigger}
        {triggerOpen && dialog}
      </>
    )
  }

  return dialog
}
